import React, { useState } from 'react';
import { Button, Collapsible } from '@printdeal/ui-library';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelectorMachineContext, useSelectorMachineSend } from '@printdeal/product-selector';
import DetermineDeliveryTool from '../../../detail/DetermineDeliveryTool';
import type { SelectedDeliveryTool } from '../../../../../types/file-delivery/FileDelivery';
import { setInLocalStorage } from '../../../../../helpers/window';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import ServiceSelectionSection from '../../../detail/advanced/FileServicesSection';
import { useScrollToSummary } from '../../../../../hooks/useScrollOnPriceSelect';
import { FileDeliveryAndServiceProps } from './file-delivery-and-service-types';

export const FileDeliveryAndServiceSelection = ({
  productPage,
  selector,
  hasFileDeliveryChoice,
  setSelectedDeliveryTool,
  selectedDeliveryTool,
  services,
  setSelectedServices,
  selectedServices,
  setIsChoiceSkipped,
}: FileDeliveryAndServiceProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);
  const price = useSelectorMachineContext((state) => state.context.price);
  const merchantRules = useSelectorMachineContext((state) => state.context.merchantRules);
  const { deliveryChoiceRef } = useScrollToSummary({ selectedPrice: price });
  const send = useSelectorMachineSend();
  const { deliveryTool: merchantDeliveryTool } = merchantRules;
  // This is set to default 'on' for EXP1021
  const defaultDeliveryTool = {
    // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
    checkmate: 'on',
    editor: 'on',
  };

  const deliveryTool = { ...defaultDeliveryTool, ...merchantDeliveryTool };
  const isDirectUpload = Boolean(deliveryTool?.directupload);
  // don't show the file services when 'directUpload' is true, and this will only be configured in MRC for exp1692
  const hasServices = deliveryTool?.servicekeys && services.length > 0 && !isDirectUpload;

  const handleDeliveryToolChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedDeliveryTool(value as SelectedDeliveryTool);
    setInLocalStorage('deliveryTool', value);
    dispatchDataLayerEntry(
      'select_file_delivery_tool',
      'click',
      {
        deliver_tool: deliveryTool,
        product_name: productPage.productNameOriginal,
        contentfulId: productPage.contentful_id,
        variant: selector.configuration.variantSku,
      },
    );
  };

  const handleSkipDeliveryToolChoice = () => {
    setIsChoiceSkipped(true);
    setIsOpen(false);
    setSelectedDeliveryTool(undefined);
    setSelectedServices([]);
    send({ type: 'Select service', selectedService: '' });
    dispatchDataLayerEntry(
      'select_file_delivery_tool',
      'click',
      {
        deliver_tool: null,
        product_name: selector.product.productNameOriginal,
        contentfulId: selector.id,
        variant: selector.configuration.variantSku,
      },
    );
  };
  return (hasServices || hasFileDeliveryChoice) ? (
    <div ref={deliveryChoiceRef} className="pd-scroll-mt-[750px] md:pd-scroll-mt-24 xl:pd-scroll-mt-64">
      <Collapsible id="delivery-tool-collapsible" isOpen={isOpen}>
        <Collapsible.Header
          button={<Collapsible.Button onClick={() => setIsOpen(!isOpen)} />}
          title={hasFileDeliveryChoice
            ? intl.formatMessage({ id: 'product.configurator.determineDeliveryTool', defaultMessage: 'How are you going deliver your design?' })
            : intl.formatMessage({ id: 'product.configurator.services', defaultMessage: 'Select your service' })}
        />
        <Collapsible.Panel className="pd-flex pd-flex-col pd-gap-5">
          {hasFileDeliveryChoice && (
          <DetermineDeliveryTool
            onToolChange={handleDeliveryToolChange}
            selectedDeliveryTool={selectedDeliveryTool}
            // "Checkmatemode: checkmate" and "checkmate: on" will be renamed
            // when MRC is replaced by the business rules app
            isUploaderEnabled={deliveryTool.checkmate === 'on'}
            isEditorEnabled={deliveryTool.editor === 'on'}
            isAltUploadBeforeOrderEnabled={deliveryTool.altuploadbeforeorder === 'on'}
            servicesEnabled={Boolean(services)}
          />
          )}
          {hasServices && (
          <ServiceSelectionSection
            services={services}
            selectedDeliveryTool={selectedDeliveryTool}
            setIsChoiceSkipped={setIsChoiceSkipped}
            setSelectedDeliveryTool={setSelectedDeliveryTool}
            selectedServices={selectedServices}
            setSelectedServices={setSelectedServices}
          />
          )}
          <hr />
          <div className="pd-flex pd-items-center pd-gap-2 pd-self-end">
            <span className="pd-text-dim-500">
              <FormattedMessage id="delivery.tool.skipChoice.title" defaultMessage="You don't want to add your design until later?" />
            </span>
            <Button variant="dim" onClick={handleSkipDeliveryToolChoice}>
              <FormattedMessage id="delivery.tool.skipChoice.button" defaultMessage="Skip choice" />
            </Button>
          </div>
        </Collapsible.Panel>
      </Collapsible>
    </div>
  ) : null;
};
